import Container from '@/components/Container';
import { CenteredSectionTitle, SectionTitle } from '@/components/SectionTitle';
import styled from 'styled-components';

/**
 * Shared shell for every tracking-insight data section.
 *
 * The design alternates a plain and a tinted background down the page so adjacent sections stay
 * distinguishable without adding dividers; `$tinted` is what a caller flips to keep that rhythm.
 */
export const InsightsSection = styled.section<{ $tinted?: boolean }>(({ theme, $tinted }) => ({
  backgroundColor: $tinted ? theme.colors.surface01 : theme.colors.highEmphasisWhite,
  paddingTop: theme.spacing[16],
  paddingBottom: theme.spacing[16],

  [`${theme.mediaBreakpointUp.md}`]: {
    paddingTop: theme.spacing[64],
    paddingBottom: theme.spacing[64],
  },
}));

/**
 * The design does not use one content width throughout.
 *
 * The card grids and the carrier header run 1160px wide (140px margins at 1440); the reviews sit
 * 20px further in on each side, at 1120px — which is what the shared `Container` already gives.
 * `$wide` widens the box by those 40px rather than re-implementing the container.
 */
export const InsightsContainer = styled(Container)<{ $wide?: boolean }>(({ theme, $wide }) => ({
  padding: `0 ${theme.spacing[16]}`,

  [`${theme.mediaBreakpointUp.md}`]: {
    ...($wide ? { maxWidth: 1200 } : {}),
    padding: `0 ${theme.spacing[20]}`,
  },
}));

/**
 * `SectionTitle` ships 16px of space under it on mobile and 24px from `md` up. Each section in
 * this design wants a different pair, so each gets its own variant rather than one of them
 * quietly drifting off-spec.
 */
export const GridSectionTitle = styled(CenteredSectionTitle)(({ theme }) => ({
  marginBottom: theme.spacing[24],
}));

export const ReviewsSectionTitle = styled(CenteredSectionTitle)(({ theme }) => ({
  marginBottom: theme.spacing[24],

  [`${theme.mediaBreakpointUp.md}`]: {
    marginBottom: theme.spacing[32],
  },
}));

export const PartnersSectionTitle = styled(CenteredSectionTitle)(({ theme }) => ({
  marginBottom: theme.spacing[32],

  [`${theme.mediaBreakpointUp.md}`]: {
    marginBottom: theme.spacing[32],
  },
}));

export const CarrierSectionTitle = styled(SectionTitle)(({ theme }) => ({
  marginBottom: theme.spacing[16],

  [`${theme.mediaBreakpointUp.md}`]: {
    marginBottom: theme.spacing[16],
  },
}));
